import {
  darkModeColors,
  ElementsEnvironmentContextProvider,
  ElementsUIContextProvider,
  LocalThemeContextProvider,
} from '@elements-financial/elements';
import { Outlet, useSearchParams } from '@remix-run/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTypedLoaderData } from 'remix-typedjson';
import { RootDocument } from './components';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type rootLoader } from './loaders';
import Cookies from 'universal-cookie';
import { useMemo } from 'react';

/**
 * ELE-929: Persist the tokens provided by the native shell as URL query params
 * to cookies **synchronously** during render, before any query child mounts.
 *
 * Previously this was done in a `useEffect`, which meant child components
 * could render — and their `useQuery` hooks could fire GraphQL requests —
 * with either no cookie at all (fresh install) or a stale cookie left by a
 * prior client session on the same device (embedded WKWebView). See ticket
 * for the observed 48 unauthenticated first-wave requests during
 * Andre Kressler's session on 2026-07-27.
 *
 * `document.cookie` is only available in the browser, so this is a no-op
 * during SSR. That is fine: the query middleware also reads the in-memory
 * `authentication` value from `ElementsEnvironmentContext`, which is
 * populated from the same URL params on both server and client.
 */
const writeAuthCookiesSync = (accessToken: string, refreshToken: string): void => {
  if (typeof document === 'undefined') return;
  const cookies = new Cookies();
  cookies.set(
    '__accessToken',
    btoa(JSON.stringify({ access_token: accessToken })),
    {
      path: '/',
      sameSite: 'lax',
      httpOnly: false,
      secure: process.env.NODE_ENV === 'production',
    }
  );
  cookies.set(
    '__refreshToken',
    btoa(JSON.stringify({ refresh_token: refreshToken })),
    {
      path: '/',
      sameSite: 'lax',
      httpOnly: false,
      secure: process.env.NODE_ENV === 'production',
    }
  );
};

export const RootPage = () => {
  const { isDev, apiUrl } = useTypedLoaderData<typeof rootLoader>();
  const [searchParams] = useSearchParams();

  const accessToken = searchParams.get('accessToken') ?? '';
  const refreshToken = searchParams.get('refreshToken') ?? '';
  const householdID = searchParams.get('householdID');

  // Write cookies synchronously during render so the first child query
  // fires with the correct auth (see writeAuthCookiesSync docstring above).
  // useMemo with a token-derived key makes this idempotent per token pair
  // and replaces any stale prior-session cookie *before* the first request.
  useMemo(() => {
    if (accessToken) {
      writeAuthCookiesSync(accessToken, refreshToken);
    }
  }, [accessToken, refreshToken]);

  // Build a fresh QueryClient whenever the access token changes so no cached
  // data from a prior session can leak into the new one. `invalidateQueries`
  // is not enough here: it marks entries stale but continues to return the
  // prior data while the background refetch is in flight, which — across a
  // token transition — briefly shows another session's data. The `key` on
  // QueryClientProvider forces the tree to remount, cancelling in-flight
  // work cleanly.
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: true,
            // Disable cache for now until we fully migrate to react-query
            cacheTime: 0,
          },
        },
      }),
    [accessToken]
  );

  // Gate the query tree on auth availability: do not render children (and
  // therefore do not let any `useQuery` fire) until we have a token from
  // the native shell. Without this, a page hit without URL auth would race
  // against a stale cookie from a prior client on the same device.
  if (!accessToken) {
    return <RootDocument>{null}</RootDocument>;
  }

  return (
    <RootDocument>
      <ElementsEnvironmentContextProvider
        app={'client'}
        isDev={isDev}
        apiUrl={apiUrl}
        authentication={{
          accessToken: accessToken,
          refreshToken: refreshToken,
        }}
        householdID={householdID}
      >
        <LocalThemeContextProvider paletteSeedColor={darkModeColors.accent}>
          <ElementsUIContextProvider>
            <QueryClientProvider key={accessToken} client={queryClient}>
              <Outlet />
              <ReactQueryDevtools position="bottom-right" initialIsOpen={false} />
            </QueryClientProvider>
          </ElementsUIContextProvider>
        </LocalThemeContextProvider>
      </ElementsEnvironmentContextProvider>
    </RootDocument>
  );
};
